import { useContext, useEffect, useState } from 'react';
import UserContext from 'components/context/UserContext';
import { useQuery, useMutation } from '@apollo/client';
import CompanySettingCategory, { CompanySettingkey } from 'utils/enum/CompanySettingEnum';
import CompanyQuery from 'services/graphQL/query/setting/CompanyQuery';
import CompanyMutation from 'services/graphQL/mutate/CompanyMutation';
import { FetchPolicy } from 'utils/enum/Core';
import ModalUtils from 'utils/ModalUtils';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import ManheimService from 'services/modules/ManheimService';

const useManheimSetting = (canWrite = false) => {
    const context = useContext(UserContext);
    const { userInformation = {} } = context ?? {};

    const [manheimClientId, setManheimClientId] = useState('');
    const [manheimDefaultLot, setManheimDefaultLot] = useState('');
    const [manheimSellerCustomField, setManheimSellerCustomField] = useState('');
    const [manheimLocationStatusCustomField, setManheimLocationStatusCustomField] = useState('');
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState('');

    const availableLots = (userInformation.lots || []).map((item) => ({ value: item.lotId, label: item.lotName }));

    const {
        data: companySettingsData,
        error: companySettingsError,
        loading: loadingCompanySettings,
    } = useQuery(CompanyQuery.GET_COMPANY_SETTINGS, {
        variables: {
            category: CompanySettingCategory.MANHEIM,
            key: [
                CompanySettingkey.MANHEIM_CLIENT_ID,
                CompanySettingkey.MANHEIM_DEFAULT_LOT,
                CompanySettingkey.MANHEIM_USER_IMPORT,
                CompanySettingkey.MANHEIM_SELLER_CUSTOM_FIELD,
                CompanySettingkey.MANHEIM_LOCATION_STATUS_CUSTOM_FIELD,
            ],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: inventoryMetaData,
        loading: inventoryMetaDataLoading,
        error: inventoryMetaDataError,
    } = useQuery(InventoryQuery.GET_INVENTORY_METADATA, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [updateCompanyManheim, {
        loading: isSavingCompanySettings,
    }] = useMutation(CompanyMutation.UPDATE_COMPANY_MANHEIM, {
        onCompleted: () => {
            ModalUtils.successMessage(null, 'Manheim settings saved successfully.');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const loading = loadingCompanySettings || isSavingCompanySettings || inventoryMetaDataLoading;
    const hasLoadedCompanySettings = Array.isArray(companySettingsData?.getCompanySettings)
        && !companySettingsError;
    const manheimUserImport = companySettingsData?.getCompanySettings?.find(
        (setting) => setting.key === CompanySettingkey.MANHEIM_USER_IMPORT,
    )?.value || '';

    const customFields = (inventoryMetaData?.getInventoryMetadata || []).map((item) => ({
        label: [item?.label, item?.property].filter(Boolean).join(' - '),
        value: item?.entityMetadataId || '',
    }));

    useEffect(() => {
        if (companySettingsData && companySettingsData?.getCompanySettings) {
            const value = companySettingsData?.getCompanySettings;
            const clientIdSetting = value?.find((setting) => setting.key === CompanySettingkey.MANHEIM_CLIENT_ID)?.value || '';
            const defaultLotSetting = value?.find((setting) => setting.key === CompanySettingkey.MANHEIM_DEFAULT_LOT)?.value || '';
            const sellerCustomFieldSetting = value?.find((setting) => setting.key === CompanySettingkey.MANHEIM_SELLER_CUSTOM_FIELD)?.value || '';
            const locationStatusCustomFieldSetting = value?.find(
                (setting) => setting.key === CompanySettingkey.MANHEIM_LOCATION_STATUS_CUSTOM_FIELD,
            )?.value || '';

            setManheimClientId(clientIdSetting);
            setManheimDefaultLot(defaultLotSetting);
            setManheimSellerCustomField(sellerCustomFieldSetting);
            setManheimLocationStatusCustomField(locationStatusCustomFieldSetting);
        }
    }, [companySettingsData]);

    useEffect(() => {
        if (companySettingsError) {
            ModalUtils.errorMessage([], companySettingsError?.message);
        }
        if (inventoryMetaDataError) {
            ModalUtils.errorMessage([], inventoryMetaDataError?.message);
        }
    }, [companySettingsError, inventoryMetaDataError]);

    const handleSubmit = () => {
        if (!canWrite || !hasLoadedCompanySettings) return;

        updateCompanyManheim({
            variables: {
                input: {
                    clientId: manheimClientId,
                    lot: manheimDefaultLot,
                    sellerCustomField: manheimSellerCustomField,
                    locationStatusCustomField: manheimLocationStatusCustomField,
                },
            },
        });
    };

    const handleImportVehicles = async () => {
        if (!canWrite) return;

        setImportDialogOpen(true);
        setIsImporting(true);
        setImportResult(null);
        setImportError('');

        try {
            const manheimService = new ManheimService();
            const response = await manheimService.importVehicles(userInformation.companyCode);
            setImportResult(response.data);
        } catch (error) {
            const endpointErrors = error.response?.data?.errors;
            const endpointMessage = Array.isArray(endpointErrors)
                ? endpointErrors.map((item) => item?.message).filter(Boolean).join('\n')
                : '';
            setImportError(endpointMessage || error.response?.data?.message || error.message || 'The Manheim import failed.');
        } finally {
            setIsImporting(false);
        }
    };

    const closeImportDialog = () => {
        if (!isImporting) setImportDialogOpen(false);
    };

    return {
        manheimClientId,
        manheimDefaultLot,
        loading,
        hasLoadedCompanySettings,
        availableLots,
        manheimUserImport,
        manheimSellerCustomField,
        manheimLocationStatusCustomField,
        customFields,
        companyCode: userInformation.companyCode,
        importDialogOpen,
        isImporting,
        importResult,
        importError,
        setManheimClientId,
        setManheimDefaultLot,
        setManheimSellerCustomField,
        setManheimLocationStatusCustomField,
        handleSubmit,
        handleImportVehicles,
        closeImportDialog,
    };
};

export default useManheimSetting;
